// src/pages/pos/PosHeader.jsx (or wherever this lives)
import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
// import { Tooltip } from "antd";
// import { all_routes } from "../../Router/all_routes";
import LanguageSwitcher from "../../InitialPage/Sidebar/LanguageSwitcher";
import { useNavigate } from "react-router-dom";
import { logout } from "../../api/authApi";
import {
  Download,
  FileText,
  BarChart2,
  Users,
  List,
  Printer,
} from "react-feather";
import {
  fetchSalesReport,
  fetchCashierPerformanceReport,
  fetchProductsReport,
} from "../../api/website-api/web-report";
import {
  listVendorOrders,
  getVendorOrderById,
} from "../../api/website-api/web-pos-order";
import { fetchRefundPolicy } from "../../api/website-api/pagesApi";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
// import { saveAs } from "file-saver";
import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";

const getCurrentLang = () => {
  const lang = (localStorage.getItem("lang") || "en").toLowerCase();
  return lang.startsWith("ar") ? "ar" : "en";
};

const isArabicLang = () => getCurrentLang() === "ar";

/**
 * Convert API values safely to displayable text
 * Fixes [object Object]
 */
const extractValue = (value) => {
  if (value == null) return "-";

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (typeof value === "object") {
    // multilingual values
    const lang = getCurrentLang();

    if (value[lang] != null) return value[lang];
    if (value.value != null) return value.value;
    if (value.amount != null) return value.amount;
    if (value.total != null) return value.total;
    if (value.count != null) return value.count;
    if (value.label != null) return value.label;
    if (value.name != null) return value.name;
    if (value.title != null) return value.title;

    // first primitive value fallback
    const primitive = Object.values(value).find(
      (v) => typeof v === "string" || typeof v === "number",
    );
    if (primitive != null) return primitive;
  }

  return String(value);
};

const safeNumber = (value) => {
  const v = extractValue(value);
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(3) : v;
};

const safeText = (value) => {
  const v = extractValue(value);
  return v == null ? "-" : String(v);
};

const toSafeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];

  const candidates = [
    value.data,
    value.results,
    value.items,
    value.list,
    value.rows,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  const values = Object.values(value);
  if (
    values.length > 0 &&
    values.every((entry) => entry && typeof entry === "object" && !Array.isArray(entry))
  ) {
    return values;
  }

  return [];
};

const formatYmdForReport = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return `${day}-${month}-${year}`;
  }

  const dmyMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) return raw;

  return raw;
};

const toComparableYmd = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const ymdMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) return `${ymdMatch[1]}-${ymdMatch[2]}-${ymdMatch[3]}`;

  const dmyMatch = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyMatch) return `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;

  return "";
};

const getOrderedReportPeriod = (fromValue = "", toValue = "") => {
  const from = formatYmdForReport(fromValue);
  const to = formatYmdForReport(toValue);

  const comparableFrom = toComparableYmd(from);
  const comparableTo = toComparableYmd(to);

  if (comparableFrom && comparableTo && comparableFrom > comparableTo) {
    return [to, from];
  }

  return [from, to];
};

/**
 * Bilingual labels
 */
const reportLabelPairs = {
  salesSummary: "Sales Summary Report / تقرير ملخص المبيعات",
  period: "Period / الفترة",
  metric: "Metric / المقياس",
  value: "Value / القيمة",
  paymentMethod: "Payment Method / طريقة الدفع",
  total: "Total / الإجمالي",
  count: "Count / العدد",
  totalSales: "Total Sales / إجمالي المبيعات",
  totalOrders: "Total Orders / إجمالي الطلبات",
  totalItemsSold: "Total Items Sold / إجمالي العناصر المباعة",
  totalDiscount: "Total Discount / إجمالي الخصم",
  totalRefunds: "Total Refunds / إجمالي المرتجعات",
  netSales: "Net Sales / صافي المبيعات",
  avgOrderValue: "Average Order Value / متوسط قيمة الطلب",
  cash: "Cash / نقدي",
  card: "Card / بطاقة",
  knet: "KNET / كي نت",
  split: "Split / متعدد",
  topProducts: "Top Products Report / تقرير أفضل المنتجات",
  productId: "Product ID / رقم المنتج",
  productName: "Product Name / اسم المنتج",
  quantity: "Quantity / الكمية",
  revenue: "Revenue / الإيراد",
  discount: "Discount / الخصم",
  orders: "Orders / الطلبات",
  cashierReport: "Cashier Performance Report / تقرير أداء الكاشير",
  cashierId: "Cashier ID / رقم الكاشير",
  cashierName: "Cashier Name / اسم الكاشير",
  email: "Email / البريد الإلكتروني",
  role: "Role / الدور",
  avgOrder: "Avg Order / متوسط الطلب",
  items: "Items / العناصر",
  sessions: "Sessions / الجلسات",
  refunds: "Refunds / المرتجعات",
  refundTotal: "Refund Total / إجمالي المرتجعات",
  netSalesShort: "Net Sales / صافي المبيعات",
  date: "Date / Ø§Ù„ØªØ§Ø±ÙŠØ®",
  cashiers: "Cashiers / Ø¹Ø¯Ø¯ Ø§Ù„ÙƒØ§Ø´ÙŠØ±",
  salesSummarySheet: "Sales Summary / Ù…Ù„Ø®Øµ Ø§Ù„Ù…Ø¨ÙŠØ¹Ø§Øª",
  cashierReportsSheet: "Cashier Reports / ØªÙ‚Ø§Ø±ÙŠØ± Ø§Ù„ÙƒØ§Ø´ÙŠØ±",
  dailyBreakdownSheet: "Daily Breakdown / Ø§Ù„ØªÙØµÙŠÙ„ Ø§Ù„ÙŠÙˆÙ…ÙŠ",
  topProductsSheet: "Top Products / Ø£ÙØ¶Ù„ Ø§Ù„Ù…Ù†ØªØ¬Ø§Øª",
  cashierPerformanceSheet: "Cashier Performance / Ø£Ø¯Ø§Ø¡ Ø§Ù„ÙƒØ§Ø´ÙŠØ±",
};
const splitReportLabel = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return { en: "", ar: "" };

  const parts = text.split(" / ");
  if (parts.length >= 2) {
    return {
      en: String(parts[0] || "").trim(),
      ar: String(parts.slice(1).join(" / ") || "").trim(),
    };
  }

  return { en: text, ar: text };
};

const t = new Proxy(
  {},
  {
    get: (_, key) => {
      if (typeof key !== "string") return "";
      const rawValue = reportLabelPairs[key];
      if (rawValue == null) return key;

      const { en, ar } = splitReportLabel(rawValue);
      return getCurrentLang() === "ar" ? ar || en : en || ar;
    },
  },
);

const escapeHtml = (value) => {
  if (value == null) return "-";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

const buildTableRows = (rows) =>
  rows
    .map(
      (row) => `
        <tr>
          ${row
            .map(
              (cell) => `
                <td style="
                  border: 1px solid #d7dee3;
                  padding: 10px 12px;
                  font-size: 13px;
                  color: #222;
                  vertical-align: middle;
                ">
                  ${escapeHtml(cell)}
                </td>
              `,
            )
            .join("")}
        </tr>
      `,
    )
    .join("");

const renderHtmlToPdf = async ({ html, fileName = "report.pdf" }) => {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-10000px";
  container.style.top = "0";
  container.style.width = "900px";
  container.style.background = "#ffffff";
  container.style.padding = "24px";
  container.style.zIndex = "-1";
  container.innerHTML = html;

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/png");
    const doc = new jsPDF("p", "pt", "a4");

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const margin = 20;
    const imgWidth = pageWidth - margin * 2;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = margin;

    doc.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;
      doc.addPage();
      doc.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    doc.save(fileName);
  } finally {
    document.body.removeChild(container);
  }
};

const invoiceLabels = {
  en: {
    invoice: "Invoice",
    orderId: "Order ID",
    date: "Date",
    paymentType: "Payment Type",
    cashier: "Payment Receiver",
    items: "Items",
    qty: "Qty",
    price: "Price",
    lineTotal: "Total",
    subtotal: "Subtotal",
    total: "Total",
    received: "Received",
    paying: "Paying",
    change: "Change",
    thankYou: "Thank you for your purchase!",
    invoiceNo: "Invoice No",
    refundPolicy: "Refund Policy",
    printSave: "Print / Save as PDF",
    back: "Back",
    printTip: "Tip: In the print dialog choose Save as PDF to download.",
    noPolicy: "Refund policy is not available.",
    splitDetails: "Split Payment Details",
    splitMethod: "Method",
    splitAmount: "Amount",
    splitReference: "Reference",
    splitNoReference: "-",
  },
  ar: {
    invoice: "\u0641\u0627\u062a\u0648\u0631\u0629",
    orderId: "\u0631\u0642\u0645 \u0627\u0644\u0637\u0644\u0628",
    date: "\u0627\u0644\u062a\u0627\u0631\u064a\u062e",
    paymentType: "\u0637\u0631\u064a\u0642\u0629 \u0627\u0644\u062f\u0641\u0639",
    cashier: "\u0627\u0644\u0645\u0633\u062a\u0644\u0645",
    items: "\u0627\u0644\u0639\u0646\u0627\u0635\u0631",
    qty: "\u0627\u0644\u0643\u0645\u064a\u0629",
    price: "\u0627\u0644\u0633\u0639\u0631",
    lineTotal: "\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a",
    subtotal: "\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0641\u0631\u0639\u064a",
    total: "\u0627\u0644\u0625\u062c\u0645\u0627\u0644\u064a",
    received: "\u0627\u0644\u0645\u0628\u0644\u063a \u0627\u0644\u0645\u0633\u062a\u0644\u0645",
    paying: "\u0627\u0644\u0645\u0628\u0644\u063a \u0627\u0644\u0645\u062f\u0641\u0648\u0639",
    change: "\u0627\u0644\u0628\u0627\u0642\u064a",
    thankYou: "\u0634\u0643\u0631\u0627\u064b \u0644\u062a\u0633\u0648\u0642\u0643\u0645!",
    invoiceNo: "\u0631\u0642\u0645 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629",
    refundPolicy: "\u0633\u064a\u0627\u0633\u0629 \u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639",
    printSave: "\u0637\u0628\u0627\u0639\u0629 / \u062d\u0641\u0638 PDF",
    back: "\u0631\u062c\u0648\u0639",
    printTip:
      "\u0645\u0644\u0627\u062d\u0638\u0629: \u0645\u0646 \u0646\u0627\u0641\u0630\u0629 \u0627\u0644\u0637\u0628\u0627\u0639\u0629 \u0627\u062e\u062a\u0631 \u062d\u0641\u0638 \u0643\u0645\u0644\u0641 PDF \u0644\u0644\u062a\u0646\u0632\u064a\u0644.",
    noPolicy:
      "\u0633\u064a\u0627\u0633\u0629 \u0627\u0644\u0627\u0633\u062a\u0631\u062c\u0627\u0639 \u063a\u064a\u0631 \u0645\u062a\u0648\u0641\u0631\u0629.",
    splitDetails:
      "\u062a\u0641\u0627\u0635\u064a\u0644 \u0627\u0644\u062f\u0641\u0639 \u0627\u0644\u0645\u062c\u0632\u0623",
    splitMethod: "\u0627\u0644\u0637\u0631\u064a\u0642\u0629",
    splitAmount: "\u0627\u0644\u0645\u0628\u0644\u063a",
    splitReference: "\u0627\u0644\u0645\u0631\u062c\u0639",
    splitNoReference: "-",
  },
};

const invoiceNumber = (value = 0, lang = "en") => {
  const n = Number(value || 0);
  return (Number.isFinite(n) ? n : 0).toLocaleString(
    lang === "ar" ? "ar-SA" : "en-US",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  );
};

const firstPresent = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const parsed = extractValue(value);
    if (parsed == null) continue;
    if (typeof parsed === "string" && !parsed.trim()) continue;
    return parsed;
  }
  return "";
};

const toNum = (value, fallback = 0) => {
  const n = Number(extractValue(value));
  return Number.isFinite(n) ? n : fallback;
};

const normalizePolicyText = (value) => {
  if (!value) return "";

  const raw = String(value).trim();

  const withBreaks = raw
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|tr|table)>/gi, "\n");

  const stripped = withBreaks.replace(/<[^>]*>/g, " ");

  const decoded = stripped
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&#039;/gi, "'");

  return decoded
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n");
};

const detectOrderLang = (order = {}) => {
  const raw = String(
    firstPresent(
      order.language_code,
      order.language,
      order.lang,
      order.languageCode,
      order.language_id,
      order.languageId,
    ) || "",
  ).toLowerCase();

  if (raw.startsWith("ar") || raw === "4" || raw.includes("arabic")) return "ar";
  if (raw.startsWith("en") || raw === "1" || raw.includes("english")) return "en";

  return getCurrentLang();
};

const resolveInvoiceLang = (order = {}) => {
  const selectedLang = getCurrentLang();
  if (selectedLang === "ar" || selectedLang === "en") return selectedLang;
  return detectOrderLang(order);
};

const getOrderId = (order = {}) =>
  String(
    firstPresent(
      order.order_id,
      order.pos_order_id,
      order.id,
      order.pk,
      order.increment_id,
      order.order_number,
    ) || "-",
  );

const paymentTypeLabel = (value) => {
  const v = String(value || "").trim();
  const k = v.toLowerCase();
  if (k.includes("cash")) return "Cash";
  if (k.includes("card")) return "Card";
  if (k.includes("knet")) return "KNET";
  if (k.includes("split")) return "Split";
  return v || "-";
};

const extractOrderItems = (order = {}) => {
  const candidateArrays = [
    order.items,
    order.order_items,
    order.orderItems,
    order.products,
    order.order_products,
    order.lines,
    order.line_items,
    order.data?.items,
  ];

  const itemsRaw = candidateArrays.find(
    (arr) => Array.isArray(arr) && arr.length > 0,
  ) || [];

  return itemsRaw.map((item) => {
    const qty = toNum(
      firstPresent(item.quantity, item.qty, item.product_quantity),
      0,
    );
    const lineTotal = toNum(
      firstPresent(item.total, item.line_total, item.subtotal),
      0,
    );
    const price = toNum(
      firstPresent(
        item.price,
        item.unit_price,
        qty > 0 ? lineTotal / qty : 0,
        item.amount,
      ),
      0,
    );

    return {
      name: String(
        firstPresent(item.name, item.product_name, item.title, item.model) ||
          "Item",
      ),
      quantity: qty || 0,
      price: price || 0,
    };
  });
};

const extractPaymentSplits = (order = {}) => {
  const candidateArrays = [
    order.payment_splits,
    order.paymentSplits,
    order.split_payments,
    order.splitPayments,
    order.payment_details,
    order.paymentDetails,
    order.payments,
    order.order?.payment_splits,
    order.order?.paymentSplits,
    order.data?.payment_splits,
    order.data?.paymentSplits,
    order.data?.split_payments,
    order.data?.splitPayments,
    order.data?.payment_details,
    order.data?.paymentDetails,
    order.data?.payments,
    order.data?.order?.payment_splits,
    order.data?.order?.paymentSplits,
  ];

  const paymentSplitsRaw = candidateArrays.find(
    (arr) => Array.isArray(arr) && arr.length > 0,
  ) || [];

  const normalized = paymentSplitsRaw
    .map((split) => ({
      method: paymentTypeLabel(
        firstPresent(
          split.payment_type,
          split.method,
          split.type,
          split.name,
          split.payment_method,
        ),
      ),
      amount: toNum(
        firstPresent(split.amount, split.paid_amount, split.value, split.total),
        0,
      ),
      reference: String(
        firstPresent(
          split.reference,
          split.ref,
          split.transaction_reference,
          split.transaction_id,
        ) || "",
      ),
    }))
    .filter(
      (split) =>
        split.method !== "-" ||
        Number(split.amount || 0) > 0 ||
        Boolean(split.reference),
    );

  if (normalized.length > 0) return normalized;

  const derived = [
    {
      method: "Cash",
      amount: toNum(
        firstPresent(order.cash_amount, order.cash_paid, order.cash, order.data?.cash_amount),
        0,
      ),
      reference: "",
    },
    {
      method: "Card",
      amount: toNum(
        firstPresent(order.card_amount, order.card_paid, order.card, order.data?.card_amount),
        0,
      ),
      reference: "",
    },
    {
      method: "KNET",
      amount: toNum(
        firstPresent(order.knet_amount, order.knet_paid, order.knet, order.data?.knet_amount),
        0,
      ),
      reference: "",
    },
  ].filter((split) => Number(split.amount || 0) > 0);

  return derived;
};

const normalizeOrderForInvoice = (order = {}) => {
  const items = extractOrderItems(order);
  const paymentSplits = extractPaymentSplits(order);
  const paymentType = paymentTypeLabel(
    firstPresent(order.payment_type, order.payment_method, order.payment_mode),
  );

  const subtotal = items.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
    0,
  );

  const total = toNum(
    firstPresent(
      order.total_amount,
      order.total,
      order.grand_total,
      order.final_total,
    ),
    subtotal,
  );

  const received = toNum(
    firstPresent(order.amount_paid, order.received_amount, order.paid_amount),
    total,
  );

  const paying = toNum(
    firstPresent(order.paying_amount, order.payable_amount, order.amount_due),
    total,
  );

  const computedChange = Math.max(0, received - paying);
  const change = toNum(firstPresent(order.change, order.change_amount), computedChange);

  return {
    orderId: getOrderId(order),
    type: paymentType,
    invoiceNo: String(
      firstPresent(
        order.invoice_no,
        order.invoice_number,
        order.reference,
        order.transaction_reference,
      ) || "",
    ),
    receivedAmount: received,
    payingAmount: paying,
    change,
    paymentReceiver: String(
      firstPresent(
        order.payment_receiver,
        order.cashier_name,
        order.created_by_name,
        order.user_name,
      ) || "-",
    ),
    totalAmount: total,
    createdAt:
      firstPresent(order.created_at, order.date_added, order.createdAt) ||
      new Date().toISOString(),
    items,
    paymentSplits,
    isSplitPayment:
      paymentType.toLowerCase().includes("split") || paymentSplits.length > 0,
  };
};

const buildInvoiceHtml = (tx, lang = "en", refundPolicy = "") => {
  const L = invoiceLabels[lang] || invoiceLabels.en;
  const enLabels = invoiceLabels.en;
  const dir = lang === "ar" ? "rtl" : "ltr";
  const alignStart = lang === "ar" ? "right" : "left";
  const alignEnd = lang === "ar" ? "left" : "right";

  const subtotal2 = (tx.items || []).reduce(
    (sum, it) => sum + Number(it.price || 0) * Number(it.quantity || 0),
    0,
  );

  const total =
    typeof tx.totalAmount === "number" ? tx.totalAmount : Number(tx.totalAmount || subtotal2);
  const received = Number(tx.receivedAmount ?? 0);
  const paying = Number(tx.payingAmount ?? total);
  const changeVal = Number(tx.change ?? Math.max(0, received - paying));

  const todayStr = new Date(tx.createdAt || Date.now()).toLocaleString(
    lang === "ar" ? "ar-SA" : "en-US",
  );

  const rows = (tx.items || [])
    .map(
      (it) => `
      <tr>
        <td>${escapeHtml(it.name || "")}</td>
        <td style="text-align:center">${invoiceNumber(it.quantity || 0, lang)}</td>
        <td style="text-align:${alignEnd}">${invoiceNumber(it.price || 0, lang)}</td>
        <td style="text-align:${alignEnd}">${invoiceNumber(
          (it.price || 0) * (it.quantity || 0),
          lang,
        )}</td>
      </tr>`,
    )
    .join("");

  const invoiceNoRow = tx.invoiceNo
    ? `<div><span>${L.invoiceNo}</span><strong>${escapeHtml(tx.invoiceNo)}</strong></div>`
    : "";
  const hasSplitRows = Array.isArray(tx.paymentSplits) && tx.paymentSplits.length > 0;
  const splitDetailsSection =
    (tx.isSplitPayment || hasSplitRows) && hasSplitRows
      ? `
    <div class="split-block">
      <div class="split-title">${L.splitDetails || enLabels.splitDetails}</div>
      <table class="split-table">
        <thead>
          <tr>
            <th class="start">${L.splitMethod || enLabels.splitMethod}</th>
            <th class="end">${L.splitAmount || enLabels.splitAmount}</th>
            <th class="start">${L.splitReference || enLabels.splitReference}</th>
          </tr>
        </thead>
        <tbody>
          ${tx.paymentSplits
            .map(
              (split) => `
            <tr>
              <td>${escapeHtml(split.method || "-")}</td>
              <td class="end">${invoiceNumber(split.amount || 0, lang)}</td>
              <td>${escapeHtml(split.reference || L.splitNoReference || enLabels.splitNoReference)}</td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `
      : "";

  const suggestedFileName = `Invoice-${tx.orderId || "order"}.pdf`;
  const storeWebsiteUrl = "https://nomination.kw/";
  const policyText = normalizePolicyText(refundPolicy || L.noPolicy);
  const policyHtml = escapeHtml(policyText).replace(/\n/g, "<br/>");

  return `
<!DOCTYPE html>
<html lang="${lang}" dir="${dir}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width,initial-scale=1" />
<title>${L.invoice} - ${escapeHtml(tx.orderId || "")}</title>
<style>
  :root { color-scheme: light; }
  * { box-sizing: border-box; }
  body {
    font-family: Arial, "Tahoma", "Segoe UI", sans-serif;
    margin: 0;
    padding: 12px;
    background: #f6f6f6;
    direction: ${dir};
    text-align: ${alignStart};
    color: #111827;
  }
  .wrap {
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 18px;
  }
  h1 {
    font-size: 20px;
    margin: 0 0 14px;
    text-align: center;
    color: #111827;
  }
  .meta {
    font-size: 13px;
    margin-bottom: 16px;
    background: #fafafa;
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 10px 12px;
  }
  .meta div {
    display: flex;
    gap: 8px;
    justify-content: space-between;
    padding: 5px 0;
    align-items: flex-start;
  }
  .meta span { opacity: .75; }
  .meta strong {
    text-align: ${alignEnd};
    word-break: break-word;
  }
  .split-block {
    margin: 10px 0 12px;
    border: 1px solid #ececec;
    border-radius: 10px;
    overflow: hidden;
  }
  .split-title {
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 700;
    background: #f8fafc;
    border-bottom: 1px solid #ececec;
  }
  .split-table {
    margin-top: 0;
    font-size: 12.5px;
  }
  .split-table th, .split-table td {
    padding: 8px 10px;
    border-bottom: 1px solid #f0f0f0;
  }
  .split-table tbody tr:last-child td { border-bottom: 0; }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    margin-top: 6px;
  }
  th, td {
    padding: 10px 8px;
    border-bottom: 1px dashed #ddd;
    vertical-align: top;
  }
  th {
    text-align: ${alignStart};
    font-weight: bold;
    background: #f8f8f8;
  }
  tfoot td { border: 0; padding: 7px 8px; }
  .total-row td {
    border-top: 2px solid #000;
    font-weight: bold;
    padding-top: 10px;
  }
  .thanks {
    text-align: center;
    margin-top: 16px;
    font-size: 13px;
    font-weight: 600;
  }
  .policy-plain {
    margin-top: 10px;
    text-align: center;
    font-size: 13px;
    line-height: 1.6;
    font-weight: 600;
    word-break: break-word;
    direction: ${dir};
  }
  .store-url {
    margin-top: 10px;
    text-align: center;
    font-size: 12px;
    color: #6b7280;
    word-break: break-word;
  }
  .end { text-align: ${alignEnd}; }
  .start { text-align: ${alignStart}; }
  .actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
  .btn {
    border: 0;
    border-radius: 10px;
    padding: 10px 14px;
    font-size: 14px;
    cursor: pointer;
  }
  .btn-primary { background: #0d6efd; color: #fff; }
  .btn-light { background: #f0f0f0; color: #111; }
  .hint {
    font-size: 12px;
    opacity: .75;
    text-align: center;
    margin-top: 4px;
    margin-bottom: 12px;
  }
  @media (max-width: 480px) {
    body { padding: 10px; }
    .wrap { padding: 12px; border-radius: 10px; }
    table { font-size: 12px; }
    th, td { padding: 7px 5px; }
    h1 { font-size: 16px; }
    .policy-plain { font-size: 12px; }
    .store-url { font-size: 11px; }
  }
  @media print {
    body { background: #fff; padding: 0; }
    .wrap {
      border: 0;
      border-radius: 0;
      padding: 0;
      max-width: 100%;
    }
    .actions, .hint { display: none !important; }
    .policy-plain {
      page-break-inside: avoid;
      break-inside: avoid;
      margin-top: 10px;
    }
  }
</style>
</head>
<body>
  <div class="wrap">
    <div class="actions">
      <button class="btn btn-primary" onclick="doPrint()">${L.printSave}</button>
      <button class="btn btn-light" onclick="goBack()">${L.back}</button>
    </div>
    <div class="hint">${L.printTip}</div>
    <h1>${L.invoice} / ${lang === "ar" ? "Invoice" : "\u0641\u0627\u062a\u0648\u0631\u0629"}</h1>
    <div class="meta">
      <div><span>${L.orderId}</span><strong>${escapeHtml(tx.orderId || "-")}</strong></div>
      <div><span>${L.date}</span><strong>${escapeHtml(todayStr)}</strong></div>
      <div><span>${L.paymentType}</span><strong>${escapeHtml(tx.type || "-")}</strong></div>
      ${invoiceNoRow}
      <div><span>${L.cashier}</span><strong>${escapeHtml(tx.paymentReceiver || "-")}</strong></div>
    </div>
    ${splitDetailsSection}
    <table>
      <thead>
        <tr>
          <th class="start">${L.items}</th>
          <th style="text-align:center">${L.qty}</th>
          <th class="end">${L.price}</th>
          <th class="end">${L.lineTotal}</th>
        </tr>
      </thead>
      <tbody>
        ${rows || `<tr><td colspan="4" style="text-align:center">-</td></tr>`}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="3" class="end">${L.subtotal}</td>
          <td class="end">${invoiceNumber(subtotal2, lang)}</td>
        </tr>
        <tr class="total-row">
          <td colspan="3" class="end">${L.total}</td>
          <td class="end">${invoiceNumber(total, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.received}</td>
          <td class="end">${invoiceNumber(received, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.paying}</td>
          <td class="end">${invoiceNumber(paying, lang)}</td>
        </tr>
        <tr>
          <td colspan="3" class="end">${L.change}</td>
          <td class="end">${invoiceNumber(changeVal, lang)}</td>
        </tr>
      </tfoot>
    </table>
    <div class="thanks">${L.thankYou}</div>
    <div class="policy-plain">${L.refundPolicy}: ${policyHtml}</div>
    <div class="store-url">${storeWebsiteUrl}</div>
  </div>
  <script>
    document.title = ${JSON.stringify(suggestedFileName)};
    function doPrint() { window.print(); }
    function goBack() { window.location.href = window.location.origin + "/pos"; }
  </script>
</body>
</html>
`;
};

const PosHeader = () => {
  // const route = all_routes;
  // const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [reportDateError, setReportDateError] = useState("");
  const [showOrderHistoryModal, setShowOrderHistoryModal] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [orderHistoryPage, setOrderHistoryPage] = useState(1);
  const [orderHistoryTotal, setOrderHistoryTotal] = useState(0);
  const [orderHistoryHasNext, setOrderHistoryHasNext] = useState(false);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [orderHistoryError, setOrderHistoryError] = useState("");
  const [orderHistoryStartDate, setOrderHistoryStartDate] = useState("");
  const [orderHistoryEndDate, setOrderHistoryEndDate] = useState("");
  const [orderHistoryPaymentType, setOrderHistoryPaymentType] = useState("");
  const [orderHistoryDateError, setOrderHistoryDateError] = useState("");
  const [orderHistoryFilteredCache, setOrderHistoryFilteredCache] = useState([]);
  const [orderHistoryFilterCacheKey, setOrderHistoryFilterCacheKey] = useState("");
  const [orderHistoryFilterNextPage, setOrderHistoryFilterNextPage] = useState(1);
  const [orderHistoryFilterExhausted, setOrderHistoryFilterExhausted] = useState(false);
  const [printingOrderId, setPrintingOrderId] = useState("");
  const orderHistoryPageLimit = 10;
  // const [loading, setLoading] = useState(false);

  const exportToExcel = (reportData, fileName) => {
    const workbook = XLSX.utils.book_new();
    const summary = reportData?.summary || {};
    const payment = summary?.payment_breakdown || {};

    const summaryRow = {
      [t.totalSales]: safeNumber(summary.total_sales),
      [t.totalOrders]: safeText(summary.total_orders),
      [t.totalItemsSold]: safeText(summary.total_items_sold),
      [t.totalDiscount]: safeNumber(summary.total_discount),
      [t.totalRefunds]: safeNumber(summary.total_refunds),
      [t.netSales]: safeNumber(summary.net_sales),
      [t.avgOrderValue]: safeNumber(summary.average_order_value),

      [`${t.cash} ${t.total}`]: safeNumber(payment?.cash?.total),
      [`${t.cash} ${t.count}`]: safeText(payment?.cash?.count),

      [`${t.card} ${t.total}`]: safeNumber(payment?.card?.total),
      [`${t.card} ${t.count}`]: safeText(payment?.card?.count),

      [`${t.knet} ${t.total}`]: safeNumber(payment?.knet?.total),
      [`${t.knet} ${t.count}`]: safeText(payment?.knet?.count),

      [`${t.split} ${t.total}`]: safeNumber(payment?.split?.total),
      [`${t.split} ${t.count}`]: safeText(payment?.split?.count),
    };

    const summarySheet = XLSX.utils.json_to_sheet([summaryRow]);
    XLSX.utils.book_append_sheet(workbook, summarySheet, t.salesSummarySheet);

    const cashierRows = toSafeArray(reportData?.cashier_reports).map((c) => ({
      [t.cashierId]: safeText(c.cashier_id),
      [t.cashierName]: safeText(c.cashier_name),
      [t.totalOrders]: safeText(c.total_orders),
      [t.totalSales]: safeNumber(c.total_sales),
      [t.totalItemsSold]: safeText(c.total_items_sold),
      [t.totalDiscount]: safeNumber(c.total_discount),
      [t.netSales]: safeNumber(c.net_sales),
      [t.avgOrderValue]: safeNumber(c.average_order_value),

      [`${t.cash} ${t.total}`]: safeNumber(c?.payment_breakdown?.cash?.total),
      [`${t.cash} ${t.count}`]: safeText(c?.payment_breakdown?.cash?.count),

      [`${t.knet} ${t.total}`]: safeNumber(c?.payment_breakdown?.knet?.total),
      [`${t.knet} ${t.count}`]: safeText(c?.payment_breakdown?.knet?.count),
    }));

    const cashierSheet = XLSX.utils.json_to_sheet(cashierRows);
    XLSX.utils.book_append_sheet(workbook, cashierSheet, t.cashierReportsSheet);

    const dailyRows = toSafeArray(reportData?.daily_breakdown).map((d) => ({
      [t.date]: safeText(d.date),
      [t.totalOrders]: safeText(d.total_orders),
      [t.totalSales]: safeNumber(d.total_sales),
      [t.items]: safeText(d.total_items),
      [t.cashiers]: safeText(d.cashier_count),
    }));

    const dailySheet = XLSX.utils.json_to_sheet(dailyRows);
    XLSX.utils.book_append_sheet(workbook, dailySheet, t.dailyBreakdownSheet);

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportSalesSummaryPDF = async (
    reportData,
    { selectedStartDate = "", selectedEndDate = "" } = {},
  ) => {
    const summary = reportData?.summary || {};
    const period = reportData?.period || {};
    const payment = summary?.payment_breakdown || {};
    const isArabic = isArabicLang();
    const [periodFrom, periodTo] = getOrderedReportPeriod(
      selectedStartDate || safeText(period.from),
      selectedEndDate || safeText(period.to),
    );

    const html = `
    <div style="
      font-family: Tahoma, Arial, 'Segoe UI', sans-serif;
      color: #111;
      background: #fff;
      direction: ${isArabic ? "rtl" : "ltr"};
    ">
      <h1 style="
        font-size: 28px;
        margin: 0 0 18px 0;
        font-weight: 700;
      ">
        ${escapeHtml(t.salesSummary)}
      </h1>

      <div style="
        font-size: 18px;
        margin-bottom: 24px;
      ">
        <strong>${escapeHtml(t.period)}:</strong>
        ${escapeHtml(periodFrom)} - ${escapeHtml(periodTo)}
      </div>

      <table style="
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 24px;
      ">
        <thead>
          <tr style="background: #4d84bd; color: white;">
            <th style="padding: 12px; text-align: ${isArabic ? "right" : "left"};">${escapeHtml(t.metric)}</th>
            <th style="padding: 12px; text-align: ${isArabic ? "right" : "left"};">${escapeHtml(t.value)}</th>
          </tr>
        </thead>
        <tbody>
          ${buildTableRows([
            [t.totalSales, safeNumber(summary.total_sales)],
            [t.totalOrders, safeText(summary.total_orders)],
            [t.totalItemsSold, safeText(summary.total_items_sold)],
            [t.totalDiscount, safeNumber(summary.total_discount)],
            [t.totalRefunds, safeNumber(summary.total_refunds)],
            [t.netSales, safeNumber(summary.net_sales)],
            [t.avgOrderValue, safeNumber(summary.average_order_value)],
          ])}
        </tbody>
      </table>

      <table style="
        width: 100%;
        border-collapse: collapse;
      ">
        <thead>
          <tr style="background: #4d84bd; color: white;">
            <th style="padding: 12px; text-align: ${isArabic ? "right" : "left"};">${escapeHtml(t.paymentMethod)}</th>
            <th style="padding: 12px; text-align: ${isArabic ? "right" : "left"};">${escapeHtml(t.total)}</th>
            <th style="padding: 12px; text-align: ${isArabic ? "right" : "left"};">${escapeHtml(t.count)}</th>
          </tr>
        </thead>
        <tbody>
          ${buildTableRows([
            [
              t.cash,
              safeNumber(payment?.cash?.total),
              safeText(payment?.cash?.count),
            ],
            [
              t.card,
              safeNumber(payment?.card?.total),
              safeText(payment?.card?.count),
            ],
            [
              t.knet,
              safeNumber(payment?.knet?.total),
              safeText(payment?.knet?.count),
            ],
            [
              t.split,
              safeNumber(payment?.split?.total),
              safeText(payment?.split?.count),
            ],
          ])}
        </tbody>
      </table>
    </div>
  `;

    await renderHtmlToPdf({
      html,
      fileName: isArabic ? "sales-report-ar.pdf" : "sales-report-en.pdf",
    });
  };

  const exportProductsToExcel = (products, fileName) => {
    const workbook = XLSX.utils.book_new();

    const formattedRows = (products || []).map((p) => ({
      [t.productId]: safeText(p.product_id),
      [t.productName]: safeText(p.product_name),
      [t.quantity]: safeText(p.total_quantity),
      [t.revenue]: safeNumber(p.total_revenue),
      [t.discount]: safeNumber(p.total_discount),
      [t.orders]: safeText(p.order_count),
    }));

    const sheet = XLSX.utils.json_to_sheet(formattedRows);

    sheet["!cols"] = [
      { wch: 18 },
      { wch: 40 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
    ];

    XLSX.utils.book_append_sheet(workbook, sheet, t.topProductsSheet);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportProductsToPDF = async (products, title) => {
    const isArabic = isArabicLang();

    const rows = (products || []).map((p) => [
      safeText(p.product_id),
      safeText(p.product_name),
      safeText(p.total_quantity),
      safeNumber(p.total_revenue),
      safeNumber(p.total_discount),
      safeText(p.order_count),
    ]);

    const html = `
    <div style="
      font-family: Tahoma, Arial, 'Segoe UI', sans-serif;
      color: #111;
      background: #fff;
      direction: ${isArabic ? "rtl" : "ltr"};
    ">
      <h1 style="font-size: 26px; margin: 0 0 24px 0; font-weight: 700;">
        ${escapeHtml(t.topProducts)}
      </h1>

      <table style="width:100%; border-collapse:collapse;">
        <thead>
          <tr style="background:#4d84bd; color:#fff;">
            <th style="padding:12px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.productId)}</th>
            <th style="padding:12px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.productName)}</th>
            <th style="padding:12px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.quantity)}</th>
            <th style="padding:12px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.revenue)}</th>
            <th style="padding:12px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.discount)}</th>
            <th style="padding:12px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.orders)}</th>
          </tr>
        </thead>
        <tbody>
          ${buildTableRows(rows)}
        </tbody>
      </table>
    </div>
  `;

    await renderHtmlToPdf({
      html,
      fileName: `${title}.pdf`,
    });
  };

  const getReportDateError = (startDateValue = "", endDateValue = "") => {
    if (!startDateValue || !endDateValue) return "";
    if (startDateValue <= endDateValue) return "";
    return (
      window.translate?.("From date cannot be after To date.") ||
      "From date cannot be after To date."
    );
  };

  const handleClearReportDateFilter = () => {
    setReportStartDate("");
    setReportEndDate("");
    setReportDateError("");
  };

  const getLocalizedReportFileName = (baseName) =>
    `${baseName}-${getCurrentLang() === "ar" ? "ar" : "en"}`;

  const handleDownloadReport = async (type, format) => {
    const dateError = getReportDateError(reportStartDate, reportEndDate);
    if (dateError) {
      setReportDateError(dateError);
      return;
    }

    try {
      // setLoading(true);
      setReportDateError("");

      let response;

      // ============================
      // 1️⃣ SALES REPORT
      // ============================
      if (type === "sales") {
        response = await fetchSalesReport({
          start_date: reportStartDate || undefined,
          end_date: reportEndDate || undefined,
        });

        const reportData = response?.data || response;

        if (!reportData) {
          alert("No sales report data found!");
          return;
        }

        if (format === "excel") {
          exportToExcel(reportData, getLocalizedReportFileName("sales-report"));
          return;
        }

        if (format === "pdf") {
          await exportSalesSummaryPDF(reportData, {
            selectedStartDate: reportStartDate,
            selectedEndDate: reportEndDate,
          });
          return;
        }
      }

      // ============================
      // 2️⃣ PRODUCTS REPORT ✅ FIXED
      // ============================
      if (type === "products") {
        response = await fetchProductsReport({
          start_date: reportStartDate || undefined,
          end_date: reportEndDate || undefined,
        });

        const productsData = toSafeArray(response?.data || response);

        if (!productsData.length) {
          alert("No product report data found!");
          return;
        }

        if (format === "excel") {
          exportProductsToExcel(
            productsData,
            getLocalizedReportFileName("products-report"),
          );
          return;
        }

        if (format === "pdf") {
          await exportProductsToPDF(
            productsData,
            getLocalizedReportFileName("products-report"),
          );
          return;
        }
      }

      // ============================
      // 3️⃣ CASHIER REPORT ✅ FIXED
      // ============================
      if (type === "cashier") {
        response = await fetchCashierPerformanceReport({
          start_date: reportStartDate || undefined,
          end_date: reportEndDate || undefined,
        });

        const cashierList = toSafeArray(response?.data || response);

        if (!cashierList.length) {
          alert("No cashier report data found!");
          return;
        }

        if (format === "excel") {
          exportCashierToExcel(
            cashierList,
            getLocalizedReportFileName("cashier-report"),
          );
          return;
        }

        if (format === "pdf") {
          await exportCashierToPDF(
            cashierList,
            getLocalizedReportFileName("cashier-report"),
          );
          return;
        }
      }
    } catch (error) {
      console.error("Report Download Error:", error);
      alert("Failed to download report!");
    } finally {
      // setLoading(false);
    }
  };

  const exportCashierToExcel = (cashiers, fileName) => {
    const workbook = XLSX.utils.book_new();

    const formattedRows = (cashiers || []).map((c) => ({
      [t.cashierId]: safeText(c.cashier_id),
      [t.cashierName]: safeText(c.cashier_name),
      [t.email]: safeText(c.email),
      [t.role]: safeText(c.role),
      [t.totalOrders]: safeText(c.total_orders),
      [t.totalSales]: safeNumber(c.total_sales),
      [t.avgOrder]: safeNumber(c.average_order_value),
      [t.totalItemsSold]: safeText(c.total_items_sold),
      [t.sessions]: safeText(c.total_sessions),
      [t.refunds]: safeText(c.refund_count),
      [t.refundTotal]: safeNumber(c.refund_total),
      [t.netSalesShort]: safeNumber(c.net_sales),
    }));

    const sheet = XLSX.utils.json_to_sheet(formattedRows);

    sheet["!cols"] = [
      { wch: 16 },
      { wch: 28 },
      { wch: 30 },
      { wch: 20 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
      { wch: 18 },
      { wch: 18 },
    ];

    XLSX.utils.book_append_sheet(workbook, sheet, t.cashierPerformanceSheet);
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const exportCashierToPDF = async (cashiers, title) => {
    const isArabic = isArabicLang();

    const rows = (cashiers || []).map((c) => [
      safeText(c.cashier_id),
      safeText(c.cashier_name),
      safeText(c.email),
      safeText(c.role),
      safeText(c.total_orders),
      safeNumber(c.total_sales),
      safeNumber(c.average_order_value),
      safeText(c.total_items_sold),
      safeText(c.total_sessions),
      safeText(c.refund_count),
      safeNumber(c.refund_total),
      safeNumber(c.net_sales),
    ]);

    const html = `
    <div style="
      font-family: Tahoma, Arial, 'Segoe UI', sans-serif;
      color: #111;
      background: #fff;
      direction: ${isArabic ? "rtl" : "ltr"};
    ">
      <h1 style="font-size: 24px; margin: 0 0 24px 0; font-weight: 700;">
        ${escapeHtml(t.cashierReport)}
      </h1>

      <table style="width:100%; border-collapse:collapse;">
        <thead>
          <tr style="background:#4d84bd; color:#fff;">
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.cashierId)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.cashierName)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.email)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.role)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.orders)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.totalSales)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.avgOrder)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.items)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.sessions)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.refunds)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.refundTotal)}</th>
            <th style="padding:10px; text-align:${isArabic ? "right" : "left"};">${escapeHtml(t.netSalesShort)}</th>
          </tr>
        </thead>
        <tbody>
          ${buildTableRows(rows)}
        </tbody>
      </table>
    </div>
  `;

    await renderHtmlToPdf({
      html,
      fileName: `${title}.pdf`,
    });
  };

  const navigate = useNavigate();

  const handleLogout = (e) => {
    e.preventDefault();
    logout();
    navigate("/pos/signin", { replace: true });
  };

  const toggleFullscreen = (elem) => {
    elem = elem || document.documentElement;
    if (
      !document.fullscreenElement &&
      !document.mozFullScreenElement &&
      !document.webkitFullscreenElement &&
      !document.msFullscreenElement
    ) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  };

  // useEffect(() => {
  //   const handleFullscreenChange = () => {
  //     setIsFullscreen(
  //       document.fullscreenElement ||
  //         document.mozFullScreenElement ||
  //         document.webkitFullscreenElement ||
  //         document.msFullscreenElement,
  //     );
  //   };

  //   document.addEventListener("fullscreenchange", handleFullscreenChange);
  //   document.addEventListener("mozfullscreenchange", handleFullscreenChange);
  //   document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
  //   document.addEventListener("msfullscreenchange", handleFullscreenChange);

  //   return () => {
  //     document.removeEventListener("fullscreenchange", handleFullscreenChange);
  //     document.removeEventListener(
  //       "mozfullscreenchange",
  //       handleFullscreenChange,
  //     );
  //     document.removeEventListener(
  //       "webkitfullscreenchange",
  //       handleFullscreenChange,
  //     );
  //     document.removeEventListener(
  //       "msfullscreenchange",
  //       handleFullscreenChange,
  //     );
  //   };
  // }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const formattedTime = currentTime.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const currentLang = (localStorage.getItem("lang") || "en").toLowerCase();
  const isArabic = currentLang.startsWith("ar");

  const getOrderHistoryDateError = (startDateValue = "", endDateValue = "") => {
    if (!startDateValue || !endDateValue) return "";
    if (startDateValue <= endDateValue) return "";
    return (
      window.translate?.("From date cannot be after To date.") ||
      "From date cannot be after To date."
    );
  };

  const toLocalYmd = (value) => {
    if (!value) return "";

    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear();
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedDate.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    const raw = String(value).trim();
    const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    return isoMatch ? isoMatch[1] : "";
  };

  const getOrderDateValue = (order = {}) =>
    firstPresent(order.created_at, order.date_added, order.createdAt, order.date);

  const isOrderWithinDateRange = (order, startDateValue = "", endDateValue = "") => {
    const orderDate = toLocalYmd(getOrderDateValue(order));
    if (!orderDate) return false;
    if (startDateValue && orderDate < startDateValue) return false;
    if (endDateValue && orderDate > endDateValue) return false;
    return true;
  };

  const normalizePaymentTypeValue = (value = "") => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "";
    if (raw.includes("split")) return "split";
    if (raw.includes("knet")) return "knet";
    if (raw.includes("card")) return "card";
    if (raw.includes("cash")) return "cash";
    return raw;
  };

  const getOrderPaymentTypeValue = (order = {}) =>
    normalizePaymentTypeValue(
      firstPresent(order.payment_type, order.payment_method, order.payment_mode),
    );

  const isOrderMatchingPaymentType = (order, paymentTypeValue = "") => {
    const normalizedPaymentType = normalizePaymentTypeValue(paymentTypeValue);
    if (!normalizedPaymentType) return true;
    return getOrderPaymentTypeValue(order) === normalizedPaymentType;
  };

  const parseNextPage = (nextValue, currentPageValue) => {
    if (!nextValue) return Math.max(1, Number(currentPageValue || 1)) + 1;

    if (typeof nextValue === "number" && Number.isFinite(nextValue)) {
      return Math.max(1, Math.floor(nextValue));
    }

    if (typeof nextValue === "string") {
      const regexMatch = nextValue.match(/[?&]page=(\d+)/i);
      if (regexMatch?.[1]) {
        const parsed = Number(regexMatch[1]);
        if (Number.isFinite(parsed) && parsed > 0) return Math.floor(parsed);
      }

      try {
        const parsedUrl = new URL(nextValue, window.location.origin);
        const pageParam = Number(parsedUrl.searchParams.get("page"));
        if (Number.isFinite(pageParam) && pageParam > 0) return Math.floor(pageParam);
      } catch (parseError) {
        // Ignore malformed URL and use fallback below.
      }
    }

    return Math.max(1, Number(currentPageValue || 1)) + 1;
  };

  const resetOrderHistoryFilterCache = () => {
    setOrderHistoryFilteredCache([]);
    setOrderHistoryFilterCacheKey("");
    setOrderHistoryFilterNextPage(1);
    setOrderHistoryFilterExhausted(false);
  };

  const paginateOrderHistory = (
    allOrders = [],
    page = 1,
    { hasMoreUnfetched = false } = {},
  ) => {
    const safePage = Math.max(1, Number(page) || 1);
    const startIndex = (safePage - 1) * orderHistoryPageLimit;
    const endIndex = startIndex + orderHistoryPageLimit;
    const pageOrders = allOrders.slice(startIndex, endIndex);

    setOrderHistory(pageOrders);
    setOrderHistoryTotal(allOrders.length);
    setOrderHistoryHasNext(endIndex < allOrders.length || hasMoreUnfetched);
    setOrderHistoryPage(safePage);
  };

  const loadOrderHistory = async (
    page = 1,
    {
      startDateValue = orderHistoryStartDate,
      endDateValue = orderHistoryEndDate,
      paymentTypeValue = orderHistoryPaymentType,
      forceRefresh = false,
    } = {},
  ) => {
    const dateError = getOrderHistoryDateError(startDateValue, endDateValue);
    if (dateError) {
      setOrderHistoryDateError(dateError);
      return;
    }

    try {
      setOrderHistoryLoading(true);
      setOrderHistoryError("");
      setOrderHistoryDateError("");
      const normalizedPaymentType = normalizePaymentTypeValue(paymentTypeValue);
      const hasFilter = Boolean(
        startDateValue || endDateValue || normalizedPaymentType,
      );

      if (hasFilter) {
        const cacheKey = `${startDateValue}|${endDateValue}|${normalizedPaymentType}`;
        const isSameFilter =
          !forceRefresh &&
          cacheKey === orderHistoryFilterCacheKey &&
          Array.isArray(orderHistoryFilteredCache);

        let filteredResults = isSameFilter ? [...orderHistoryFilteredCache] : [];
        let nextPageToFetch = isSameFilter
          ? Math.max(1, Number(orderHistoryFilterNextPage || 1))
          : 1;
        let exhausted = isSameFilter ? Boolean(orderHistoryFilterExhausted) : false;

        const requiredCount = Math.max(1, Number(page) || 1) * orderHistoryPageLimit;
        let guard = 0;

        while (filteredResults.length < requiredCount && !exhausted && guard < 50) {
          const response = await listVendorOrders({
            page: nextPageToFetch,
            pagelimit: orderHistoryPageLimit,
            date_from: startDateValue || undefined,
            date_to: endDateValue || undefined,
            payment_type: normalizedPaymentType || undefined,
          });

          const ordersBatch = Array.isArray(response?.orders) ? response.orders : [];

          filteredResults = [
            ...filteredResults,
            ...ordersBatch.filter((order) =>
              isOrderWithinDateRange(order, startDateValue, endDateValue) &&
              isOrderMatchingPaymentType(order, normalizedPaymentType),
            ),
          ];

          const hasNextFromServer = Boolean(response?.next) && ordersBatch.length > 0;
          const batchDates = ordersBatch
            .map((order) => toLocalYmd(getOrderDateValue(order)))
            .filter(Boolean);
          const olderThanStart =
            Boolean(startDateValue) &&
            batchDates.length > 0 &&
            batchDates.every((dateValue) => dateValue < startDateValue);

          exhausted = !hasNextFromServer || olderThanStart;
          nextPageToFetch = hasNextFromServer
            ? parseNextPage(response?.next, nextPageToFetch)
            : nextPageToFetch + 1;
          guard += 1;
        }

        setOrderHistoryFilteredCache(filteredResults);
        setOrderHistoryFilterCacheKey(cacheKey);
        setOrderHistoryFilterNextPage(nextPageToFetch);
        setOrderHistoryFilterExhausted(exhausted);

        paginateOrderHistory(filteredResults, page, {
          hasMoreUnfetched: !exhausted,
        });
      } else {
        resetOrderHistoryFilterCache();

        const response = await listVendorOrders({
          page,
          pagelimit: orderHistoryPageLimit,
          payment_type: normalizedPaymentType || undefined,
        });

        setOrderHistory(Array.isArray(response?.orders) ? response.orders : []);
        setOrderHistoryTotal(Number(response?.count || 0));
        setOrderHistoryHasNext(Boolean(response?.next));
        setOrderHistoryPage(page);
      }
    } catch (error) {
      console.error("Order history fetch failed:", error);
      setOrderHistoryError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load order history.",
      );
      setOrderHistory([]);
      setOrderHistoryTotal(0);
      setOrderHistoryHasNext(false);
    } finally {
      setOrderHistoryLoading(false);
    }
  };

  const openOrderHistoryModal = () => {
    const resetFilters = {
      startDateValue: "",
      endDateValue: "",
      paymentTypeValue: "",
    };
    setOrderHistoryStartDate("");
    setOrderHistoryEndDate("");
    setOrderHistoryPaymentType("");
    setOrderHistoryDateError("");
    resetOrderHistoryFilterCache();
    setShowOrderHistoryModal(true);
    loadOrderHistory(1, resetFilters);
  };

  const closeOrderHistoryModal = () => {
    setShowOrderHistoryModal(false);
  };

  const handleApplyOrderHistoryDateFilter = () => {
    loadOrderHistory(1, {
      startDateValue: orderHistoryStartDate,
      endDateValue: orderHistoryEndDate,
      paymentTypeValue: orderHistoryPaymentType,
      forceRefresh: true,
    });
  };

  const handleResetOrderHistoryDateFilter = () => {
    const resetFilters = {
      startDateValue: "",
      endDateValue: "",
      paymentTypeValue: "",
    };
    setOrderHistoryStartDate("");
    setOrderHistoryEndDate("");
    setOrderHistoryPaymentType("");
    setOrderHistoryDateError("");
    resetOrderHistoryFilterCache();
    loadOrderHistory(1, { ...resetFilters, forceRefresh: true });
  };

  const formatOrderDate = (order = {}) => {
    const raw = firstPresent(order.created_at, order.date_added, order.createdAt);
    if (!raw) return "-";
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return String(raw);
    return date.toLocaleString(getCurrentLang() === "ar" ? "ar-SA" : "en-US");
  };

  const formatOrderTotal = (order = {}) => {
    const total = toNum(
      firstPresent(
        order.total_amount,
        order.total,
        order.grand_total,
        order.final_total,
      ),
      0,
    );
    return invoiceNumber(total, getCurrentLang());
  };

  const hasNextPage =
    orderHistoryHasNext ||
    orderHistoryPage * orderHistoryPageLimit < Number(orderHistoryTotal || 0);
  const hasOrderHistoryFilter = Boolean(
    orderHistoryStartDate || orderHistoryEndDate || orderHistoryPaymentType,
  );

  const handlePrintOrderInvoice = async (order) => {
    const orderId = getOrderId(order);
    const lang = resolveInvoiceLang(order);

    try {
      setPrintingOrderId(orderId);

      let orderToPrint = order;

      if (orderId && orderId !== "-") {
        try {
          const detailRes = await getVendorOrderById(orderId);
          const detailOrder =
            detailRes?.data?.data?.order ||
            detailRes?.data?.data ||
            detailRes?.data?.order ||
            detailRes?.order ||
            detailRes?.data ||
            detailRes ||
            {};
          const detailPaymentSplits = extractPaymentSplits(detailOrder);
          const basePaymentSplits = extractPaymentSplits(order);

          orderToPrint = {
            ...order,
            ...(typeof detailOrder === "object" ? detailOrder : {}),
            payment_splits:
              detailPaymentSplits.length > 0
                ? detailPaymentSplits
                : basePaymentSplits,
            paymentSplits:
              detailPaymentSplits.length > 0
                ? detailPaymentSplits
                : basePaymentSplits,
            payment_type:
              firstPresent(
                detailOrder?.payment_type,
                detailOrder?.payment_method,
                order?.payment_type,
                order?.payment_method,
              ) ||
              (detailPaymentSplits.length > 0 || basePaymentSplits.length > 0
                ? "split"
                : "cash"),
          };
        } catch (detailError) {
          console.warn("Order detail fetch failed, using list payload:", detailError);
        }
      }

      let refundPolicy = "";

      try {
        const policyRes = await fetchRefundPolicy(lang);
        refundPolicy =
          policyRes?.data?.description?.description ||
          policyRes?.description?.description ||
          policyRes?.data?.information_description?.description ||
          policyRes?.information_description?.description ||
          policyRes?.data?.content ||
          policyRes?.content ||
          "";
      } catch (policyError) {
        console.error("Failed to fetch refund policy for history invoice:", policyError);
      }

      const tx = normalizeOrderForInvoice(orderToPrint);
      const html = buildInvoiceHtml(tx, lang, refundPolicy);

      const w = window.open("", "_self");
      if (!w) {
        alert("Unable to open invoice.");
        return;
      }

      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (error) {
      console.error("Failed to print historical invoice:", error);
      alert(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to print invoice.",
      );
    } finally {
      setPrintingOrderId("");
    }
  };

  const translatedPosUser = window.translate?.("POS User");
  const posUserLabel =
    translatedPosUser && translatedPosUser !== "POS User"
      ? translatedPosUser
      : isArabic
        ? "مستخدم نقطة البيع"
        : "POS User";

  return (
    <>
    <header className="pos-header-pro">
      {/* ================= LEFT ================= */}
      <div className="pos-header-left">
        <ImageWithBasePath
          src="assets/img/logo.png"
          alt="Logo"
          className="pos-logo"
        />

        <div className="pos-brand">
          <h4>Hamilton POS</h4>
          <span>Point of Sale System</span>
        </div>
      </div>

      {/* ================= RIGHT ================= */}
      <div className="pos-header-right">
        {/* Clock (moved to right) */}
        <div className="time-badge small">
          <i className="ti ti-clock"></i>
          <span>{formattedTime}</span>
        </div>
        {/* Calculator */}
        <button
          className="icon-btn primary"
          data-bs-toggle="modal"
          data-bs-target="#calculator"
        >
          <i className="ti ti-calculator"></i>
        </button>

        {/* Language */}
        <div className="icon-btn">
          <LanguageSwitcher />
        </div>

        {/* Fullscreen */}
        <button className="icon-btn" onClick={() => toggleFullscreen()}>
          <i className="ti ti-maximize"></i>
        </button>

        {/* Reports */}
        <div className="dropdown">
          <button
            className="report-btn dropdown-toggle"
            data-bs-toggle="dropdown"
            data-bs-auto-close="outside"
          >
            <BarChart2 size={16} />
            <span className="d-none d-md-inline">
              {window.translate("Reports")}
            </span>
          </button>

          <div
            className="dropdown-menu dropdown-menu-end report-dropdown"
            dir={isArabic ? "rtl" : "ltr"}
          >
            <div className="report-dropdown-head">
              <h6 className="report-dropdown-title">
                <span className="report-head-icon">
                  <Download size={16} />
                </span>
                {window.translate("Download Reports")}
              </h6>
              <span className="report-lang-chip">
                {currentLang === "ar" ? "AR" : "EN"}
              </span>
            </div>

            <div className="date-inputs report-filter-panel">
              <div className="report-date-field">
                <label htmlFor="report-start-date">
                  {window.translate("From Date")}
                </label>
                <input
                  id="report-start-date"
                  type="date"
                  value={reportStartDate}
                  max={reportEndDate || undefined}
                  onChange={(e) => {
                    setReportStartDate(e.target.value);
                    setReportDateError("");
                  }}
                />
              </div>
              <div className="report-date-field">
                <label htmlFor="report-end-date">
                  {window.translate("To Date")}
                </label>
                <input
                  id="report-end-date"
                  type="date"
                  value={reportEndDate}
                  min={reportStartDate || undefined}
                  onChange={(e) => {
                    setReportEndDate(e.target.value);
                    setReportDateError("");
                  }}
                />
              </div>
              <div className="report-date-actions">
                <button
                  type="button"
                  className="mini pdf report-clear-btn"
                  onClick={handleClearReportDateFilter}
                  disabled={!reportStartDate && !reportEndDate}
                >
                  {window.translate("Clear")}
                </button>
              </div>
            </div>
            {reportDateError ? (
              <div className="report-date-error">{reportDateError}</div>
            ) : null}

            <div className="report-download-list">
            {[
              {
                label: window.translate("Sales Summary"),
                type: "sales",
                icon: <FileText size={15} />,
              },
              {
                label: window.translate("Top Products"),
                type: "products",
                icon: <BarChart2 size={15} />,
              },
              {
                label: window.translate("Cashier Report"),
                type: "cashier",
                icon: <Users size={15} />,
              },
            ].map((item) => (
              <div key={item.type} className="report-row report-row-pro">
                <span className="report-row-icon">{item.icon}</span>
                <span className="report-row-title">{item.label}</span>

                <div className="report-row-actions">
                  <button
                    className="mini pdf report-action-btn"
                    onClick={() => handleDownloadReport(item.type, "pdf")}
                  >
                    {window.translate("PDF")}
                  </button>

                  <button
                    className="mini excel report-action-btn"
                    onClick={() => handleDownloadReport(item.type, "excel")}
                  >
                    {window.translate("Excel")}
                  </button>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>

        <button className="report-btn" onClick={openOrderHistoryModal}>
          <List size={16} />
          <span className="d-none d-md-inline">
            {window.translate("Order History")}
          </span>
        </button>

        {/* User */}
        <div className="dropdown">
          <img
            src="assets/img/profiles/user-img.png"
            alt="User"
            className="user-avatar dropdown-toggle"
            data-bs-toggle="dropdown"
          />

          <div
            className="dropdown-menu dropdown-menu-end user-dropdown"
            style={{
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            <h6
              className={`fw-bold mb-2 ${isArabic ? "text-start mb-2 p-3" : "text-end mb-2 p-3"}`}
            >
              {posUserLabel}
            </h6>
            <hr />
            <button
              className={`dropdown-item text-danger ${
                isArabic ? "text-start mb-2 p-3" : "text-end mb-2 p-3"
              }`}
              style={{ textAlign: isArabic ? "right" : "left" }}
              onClick={handleLogout}
            >
              {window.translate("Logout")}
            </button>
          </div>
        </div>
      </div>
    </header>
    {showOrderHistoryModal && (
      <div className="pos-history-modal-backdrop" onClick={closeOrderHistoryModal}>
        <div
          className="pos-history-modal-card"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          dir={isArabic ? "rtl" : "ltr"}
        >
          <div className="pos-history-modal-head">
            <h5>{window.translate("Order History")}</h5>
            <button
              type="button"
              className="pos-history-close"
              onClick={closeOrderHistoryModal}
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>

          <div className="pos-history-modal-body">
            <div className="pos-history-filters">
              <div className="pos-history-filter-field">
                <label htmlFor="order-history-start">
                  {window.translate("From Date")}
                </label>
                <input
                  id="order-history-start"
                  type="date"
                  value={orderHistoryStartDate}
                  max={orderHistoryEndDate || undefined}
                  onChange={(e) => {
                    setOrderHistoryStartDate(e.target.value);
                    setOrderHistoryDateError("");
                  }}
                />
              </div>

              <div className="pos-history-filter-field">
                <label htmlFor="order-history-end">
                  {window.translate("To Date")}
                </label>
                <input
                  id="order-history-end"
                  type="date"
                  value={orderHistoryEndDate}
                  min={orderHistoryStartDate || undefined}
                  onChange={(e) => {
                    setOrderHistoryEndDate(e.target.value);
                    setOrderHistoryDateError("");
                  }}
                />
              </div>

              <div className="pos-history-filter-field">
                <label htmlFor="order-history-payment-type">
                  {window.translate("Payment Type")}
                </label>
                <select
                  id="order-history-payment-type"
                  value={orderHistoryPaymentType}
                  onChange={(e) => {
                    setOrderHistoryPaymentType(e.target.value);
                  }}
                >
                  <option value="">{window.translate("All")}</option>
                  <option value="cash">{window.translate("Cash")}</option>
                  <option value="card">{window.translate("Card")}</option>
                  <option value="knet">{window.translate("KNET")}</option>
                  <option value="split">{window.translate("Split")}</option>
                </select>
              </div>

              <div className="pos-history-filter-actions">
                <button
                  type="button"
                  className="mini excel"
                  disabled={orderHistoryLoading}
                  onClick={handleApplyOrderHistoryDateFilter}
                >
                  {window.translate("Apply")}
                </button>
                <button
                  type="button"
                  className="mini pdf"
                  disabled={orderHistoryLoading || !hasOrderHistoryFilter}
                  onClick={handleResetOrderHistoryDateFilter}
                >
                  {window.translate("Clear")}
                </button>
              </div>
            </div>

            {orderHistoryDateError ? (
              <div className="pos-history-filter-error">{orderHistoryDateError}</div>
            ) : null}

            {orderHistoryLoading ? (
              <div className="pos-history-empty">
                {window.translate("Loading")}...
              </div>
            ) : orderHistoryError ? (
              <div className="pos-history-error">{orderHistoryError}</div>
            ) : !orderHistory.length ? (
              <div className="pos-history-empty">
                {window.translate("No past transactions found.")}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm pos-history-table">
                  <thead>
                    <tr>
                      <th>{window.translate("Order ID")}</th>
                      <th>{window.translate("Date")}</th>
                      <th>{window.translate("Payment Type")}</th>
                      <th>{window.translate("Language")}</th>
                      <th className="text-end">{window.translate("Total")}</th>
                      <th className="text-end">{window.translate("Action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderHistory.map((order, index) => {
                      const id = getOrderId(order);
                      const lang = resolveInvoiceLang(order);
                      const isPrinting = printingOrderId === id;

                      return (
                        <tr key={`${id}-${index}`}>
                          <td>{id}</td>
                          <td>{formatOrderDate(order)}</td>
                          <td>
                            {paymentTypeLabel(
                              firstPresent(
                                order.payment_type,
                                order.payment_method,
                                order.payment_mode,
                              ),
                            )}
                          </td>
                          <td>
                            <span className="pos-lang-chip">
                              {lang === "ar" ? "AR" : "EN"}
                            </span>
                          </td>
                          <td className="text-end">{formatOrderTotal(order)}</td>
                          <td className="text-end">
                            <button
                              type="button"
                              className="pos-history-print-btn"
                              onClick={() => handlePrintOrderInvoice(order)}
                              title={window.translate("Print Invoice")}
                              disabled={isPrinting}
                            >
                              <Printer size={14} />
                              <span>
                                {isPrinting
                                  ? window.translate("Printing...")
                                  : window.translate("Print")}
                              </span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="pos-history-modal-foot">
            <button
              type="button"
              className="mini excel"
              disabled={orderHistoryLoading || orderHistoryPage <= 1}
              onClick={() => loadOrderHistory(orderHistoryPage - 1)}
            >
              {window.translate("Previous")}
            </button>
            <span className="pos-history-page">
              {window.translate("Page")} {orderHistoryPage}
            </span>
            <button
              type="button"
              className="mini pdf"
              disabled={orderHistoryLoading || !hasNextPage}
              onClick={() => loadOrderHistory(orderHistoryPage + 1)}
            >
              {window.translate("Next")}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default PosHeader;


